/**
 * Cloud-save client: Google sign-in (web, via Google Identity Services) +
 * pull/merge/push of the local save against the nodus-sync worker.
 *
 * Dark when `authConfigured()` is false (no GOOGLE_CLIENT_ID) — every method is
 * a safe no-op, so call sites never need to guard. The merge authority lives on
 * the CLIENT: we pull the cloud save, `mergeStates(local, remote)`, adopt the
 * result locally, then push it back. The worker just stores the blob.
 *
 * Native (Capacitor) sign-in is a follow-up — `signIn()` resolves null there
 * for now; the web build is the live target.
 */
import { Capacitor } from '@capacitor/core';
import { Progress, mergeStates, type State } from './progress';
import { GOOGLE_CLIENT_ID, SYNC_API_URL, authConfigured } from './auth-config';

export interface CloudUser {
  sub: string;
  email?: string;
  name?: string;
  picture?: string;
}

// ── Minimal Google Identity Services typings ────────────────────────────────
interface GisCredentialResponse { credential: string }
interface GisId {
  initialize(cfg: {
    client_id: string;
    callback: (r: GisCredentialResponse) => void;
    auto_select?: boolean;
    cancel_on_tap_outside?: boolean;
    use_fedcm_for_prompt?: boolean;
  }): void;
  prompt(): void;
  renderButton(el: HTMLElement, opts: Record<string, unknown>): void;
  disableAutoSelect(): void;
}
declare global {
  interface Window { google?: { accounts?: { id?: GisId } } }
}

const USER_KEY = 'nodefall.cloud.user';

let idToken: string | null = null;        // live Google ID token (this session)
let user: CloudUser | null = null;
let gisReady: Promise<GisId> | null = null;
let initialized = false;
let pendingSignIn: ((r: GisCredentialResponse | null) => void) | null = null;

/** Decode a JWT payload (no verification — the worker verifies). */
function decodeJwt(token: string): Record<string, unknown> | null {
  try {
    const part = token.split('.')[1];
    const b64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(b64)
        .split('')
        .map((ch) => '%' + ('00' + ch.charCodeAt(0).toString(16)).slice(-2))
        .join(''),
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function loadStoredUser(): CloudUser | null {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? (JSON.parse(raw) as CloudUser) : null;
  } catch {
    return null;
  }
}

function storeUser(u: CloudUser | null) {
  try {
    if (u) localStorage.setItem(USER_KEY, JSON.stringify(u));
    else localStorage.removeItem(USER_KEY);
  } catch {
    /* ignore */
  }
}

function ensureGis(): Promise<GisId> {
  if (gisReady) return gisReady;
  gisReady = new Promise<GisId>((resolve, reject) => {
    if (window.google?.accounts?.id) return resolve(window.google.accounts.id);
    const s = document.createElement('script');
    s.src = 'https://accounts.google.com/gsi/client';
    s.async = true;
    s.defer = true;
    s.onload = () => {
      const id = window.google?.accounts?.id;
      id ? resolve(id) : reject(new Error('GIS loaded but accounts.id missing'));
    };
    s.onerror = () => reject(new Error('failed to load Google Identity Services'));
    document.head.appendChild(s);
  });
  return gisReady;
}

function initGis(id: GisId) {
  if (initialized) return;
  id.initialize({
    client_id: GOOGLE_CLIENT_ID,
    callback: (r) => {
      idToken = r.credential;
      const claims = decodeJwt(r.credential);
      if (claims && typeof claims.sub === 'string') {
        user = {
          sub: claims.sub,
          email: typeof claims.email === 'string' ? claims.email : undefined,
          name: typeof claims.name === 'string' ? claims.name : undefined,
          picture: typeof claims.picture === 'string' ? claims.picture : undefined,
        };
        storeUser(user);
      }
      const p = pendingSignIn;
      pendingSignIn = null;
      p?.(r);
    },
    auto_select: false,
    cancel_on_tap_outside: true,
    use_fedcm_for_prompt: true,
  });
  initialized = true;
}

/**
 * A DOM overlay hosting the official Google button — the most reliable web
 * sign-in (One Tap can be silently suppressed). Resolves when the user signs in
 * (via button or One Tap) or dismisses the overlay.
 */
function showSignInOverlay(id: GisId): Promise<GisCredentialResponse | null> {
  return new Promise((resolve) => {
    pendingSignIn = (r) => {
      cleanup();
      resolve(r);
    };
    const backdrop = document.createElement('div');
    backdrop.style.cssText =
      'position:fixed;inset:0;z-index:99999;display:flex;align-items:center;justify-content:center;' +
      'background:rgba(5,6,13,0.86);backdrop-filter:blur(4px);font-family:system-ui,sans-serif';
    const card = document.createElement('div');
    card.style.cssText =
      'background:#0d0e1a;border:1px solid #2a2c44;border-radius:14px;padding:28px 26px;text-align:center;' +
      'box-shadow:0 0 40px rgba(0,255,255,0.12);max-width:320px';
    const title = document.createElement('div');
    title.textContent = 'SYNC YOUR PROGRESS';
    title.style.cssText = 'color:#e8ecff;letter-spacing:2px;font-size:15px;margin-bottom:6px;font-weight:700';
    const sub = document.createElement('div');
    sub.textContent = 'Sign in to carry your campaign, streaks and best runs across devices.';
    sub.style.cssText = 'color:#8a90b8;font-size:12px;line-height:1.5;margin-bottom:18px';
    const btnHost = document.createElement('div');
    btnHost.style.cssText = 'display:flex;justify-content:center';
    const close = document.createElement('button');
    close.textContent = 'Not now';
    close.style.cssText =
      'margin-top:16px;background:none;border:none;color:#6a7099;font-size:12px;cursor:pointer;text-decoration:underline';
    close.onclick = () => {
      const p = pendingSignIn;
      pendingSignIn = null;
      cleanup();
      p?.(null);
    };
    card.append(title, sub, btnHost, close);
    backdrop.appendChild(card);
    document.body.appendChild(backdrop);

    function cleanup() {
      backdrop.remove();
    }

    id.renderButton(btnHost, {
      theme: 'filled_black',
      size: 'large',
      shape: 'pill',
      text: 'signin_with',
    });
    try {
      id.prompt();
    } catch {
      /* One Tap is best-effort; the rendered button is the real path. */
    }
  });
}

async function api<T>(path: string, init: RequestInit = {}): Promise<T | null> {
  if (!idToken) return null;
  try {
    const res = await fetch(`${SYNC_API_URL}${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
        ...(init.headers || {}),
      },
    });
    if (res.status === 401) {
      // token expired/invalid — drop it so the next action re-prompts
      idToken = null;
      return null;
    }
    if (!res.ok) return null;
    return (await res.json()) as T;
  } catch {
    return null;
  }
}

export const Cloud = {
  configured(): boolean {
    return authConfigured();
  },

  /** The signed-in user (from this session or a remembered profile), if any. */
  currentUser(): CloudUser | null {
    return user ?? (user = loadStoredUser());
  },

  /** True only when we hold a live token good for syncing this session. */
  hasLiveSession(): boolean {
    return !!idToken;
  },

  /**
   * Trigger interactive sign-in. Resolves the user on success, null if the user
   * dismissed it or sign-in is unavailable. Web-only for now.
   */
  async signIn(): Promise<CloudUser | null> {
    if (!authConfigured()) return null;
    if (Capacitor.isNativePlatform()) return null; // native sign-in: follow-up
    try {
      const id = await ensureGis();
      initGis(id);
      const r = await showSignInOverlay(id);
      if (!r) return null;
      return user;
    } catch {
      return null;
    }
  },

  /** Forget the session + remembered profile (local data is untouched). */
  signOut() {
    idToken = null;
    user = null;
    storeUser(null);
    try {
      window.google?.accounts?.id?.disableAutoSelect();
    } catch {
      /* ignore */
    }
  },

  /**
   * Pull the cloud save, merge it with local, adopt the result, push it back.
   * Signs in first if needed. Returns the merged state, or null if not signed
   * in / offline / dark.
   */
  async syncNow(): Promise<State | null> {
    if (!authConfigured()) return null;
    if (!idToken) {
      const u = await this.signIn();
      if (!u || !idToken) return null;
    }
    const remote = await api<{ state: State | null; rev: number }>('/v1/save', { method: 'GET' });
    if (remote === null) return null; // unauthorized/offline
    const local = Progress.snapshot();
    const merged = remote.state ? mergeStates(local, remote.state) : local;
    Progress.adopt(merged);
    await api('/v1/save', { method: 'PUT', body: JSON.stringify({ state: merged }) });
    return merged;
  },

  /**
   * Best-effort push of the current local save (no pull/merge). Call after a
   * win/level-complete when already signed in; silently no-ops otherwise.
   */
  async push(): Promise<void> {
    if (!authConfigured() || !idToken) return;
    await api('/v1/save', { method: 'PUT', body: JSON.stringify({ state: Progress.snapshot() }) });
  },
};
